// 환경 효과: 풀·꽃 카드(바람), 화로 불꽃, 버섯 발광, 빛줄기. 모두 인스턴싱 또는 병합 1회 그리기.
// 배경 효과라 실시간 time을 쓴다(전투 VFX와 달리 히트스톱에 멈출 필요가 없다).
import * as THREE from 'three/webgpu';
import {
  abs, attribute, cos, float, fract, length, mix, mx_noise_float, positionGeometry, positionLocal, positionWorld, sin, smoothstep, time, transformNormalToView,
  uniform, uv, vec3,
} from 'three/tsl';
import { CAMERA } from '../../shared/config';
import { compose } from '../util';
import { kitCutNodes } from './cut';
import type { Emitter } from './layout';
import { mulberry32 } from './rng';
import { driftCount, type DriftKind } from './regionKits';

/** 카메라는 회전하지 않으므로 판을 시선에 수직으로 한 번만 기울인다 */
const FACE_CAMERA = -(CAMERA.pitchDeg * Math.PI) / 180;

function canvasTexture(draw: (g: CanvasRenderingContext2D, s: number) => void, size = 128): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const g = c.getContext('2d')!;
  draw(g, size);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.generateMipmaps = true;
  return t;
}

/** 풀 포기: 아래가 짙고 끝이 밝은 잎 14장 */
function tuftTexture(): THREE.CanvasTexture {
  return canvasTexture((g, s) => {
    const r = mulberry32(5);
    for (let i = 0; i < 16; i++) {
      const x0 = s * (0.2 + r() * 0.6), lean = (r() - 0.5) * s * 0.5, h = s * (0.5 + r() * 0.45), w = s * (0.03 + r() * 0.035);
      const gr = g.createLinearGradient(0, s, 0, s - h);
      gr.addColorStop(0, '#2f4a1c');
      gr.addColorStop(1, r() < 0.5 ? '#a9c85a' : '#86b246');
      g.fillStyle = gr;
      g.beginPath();
      g.moveTo(x0 - w, s);
      g.quadraticCurveTo(x0 + lean * 0.3, s - h * 0.6, x0 + lean, s - h);
      g.quadraticCurveTo(x0 + lean * 0.3 + w * 0.6, s - h * 0.55, x0 + w, s);
      g.fill();
    }
  });
}

/** 들꽃 무더기: 잎 + 흰·분홍·노랑·연보라 꽃 */
function flowerTexture(): THREE.CanvasTexture {
  return canvasTexture((g, s) => {
    const r = mulberry32(9);
    for (let i = 0; i < 10; i++) {
      const x0 = s * (0.15 + r() * 0.7), h = s * (0.25 + r() * 0.3);
      g.strokeStyle = '#3f6a26';
      g.lineWidth = s * 0.025;
      g.beginPath();
      g.moveTo(x0, s);
      g.quadraticCurveTo(x0 + (r() - 0.5) * s * 0.2, s - h * 0.5, x0 + (r() - 0.5) * s * 0.15, s - h);
      g.stroke();
    }
    const cols = ['#fbf6ea', '#f6b8c8', '#f5dc6e', '#c9b4f0', '#fbf6ea'];
    for (let i = 0; i < 9; i++) {
      const x = s * (0.15 + r() * 0.7), y = s * (0.3 + r() * 0.45), pr = s * (0.045 + r() * 0.03);
      g.fillStyle = cols[i % cols.length];
      for (let k = 0; k < 5; k++) {
        const a = (k / 5) * Math.PI * 2;
        g.beginPath();
        g.arc(x + Math.cos(a) * pr, y + Math.sin(a) * pr, pr * 0.75, 0, Math.PI * 2);
        g.fill();
      }
      g.fillStyle = '#e8a83a';
      g.beginPath();
      g.arc(x, y, pr * 0.55, 0, Math.PI * 2);
      g.fill();
    }
  });
}

/** 서리 더미(R3, c023): 눈 덩이 위로 얼음 조각 몇 개가 솟은 작은 카드(풀 포기 대신) */
function frostTexture(): THREE.CanvasTexture {
  return canvasTexture((g, s) => {
    const r = mulberry32(31);
    // 눈 덩이(아래 반원들)
    for (let i = 0; i < 5; i++) {
      const x = s * (0.25 + r() * 0.5), y = s * (0.9 - r() * 0.08), rr = s * (0.12 + r() * 0.12);
      const gr = g.createRadialGradient(x - rr * 0.3, y - rr * 0.4, rr * 0.1, x, y, rr);
      gr.addColorStop(0, '#ffffff');
      gr.addColorStop(1, '#a9c0de');
      g.fillStyle = gr;
      g.beginPath();
      g.arc(x, y, rr, Math.PI, 0);
      g.fill();
    }
    // 얼음 조각(위가 밝은 가는 삼각형)
    for (let i = 0; i < 6; i++) {
      const x0 = s * (0.2 + r() * 0.6), h = s * (0.3 + r() * 0.45), w = s * (0.03 + r() * 0.04), lean = (r() - 0.5) * s * 0.25;
      const gr = g.createLinearGradient(0, s * 0.92, 0, s * 0.92 - h);
      gr.addColorStop(0, '#2f6fb8');
      gr.addColorStop(0.6, '#79cdf2');
      gr.addColorStop(1, '#f0fcff');
      g.fillStyle = gr;
      g.beginPath();
      g.moveTo(x0 - w, s * 0.92);
      g.lineTo(x0 + lean, s * 0.92 - h);
      g.lineTo(x0 + w, s * 0.92);
      g.fill();
    }
  });
}

/** 늪 갈대(R4, c023): 짙은 녹갈색 가는 잎 + 부들 이삭 */
function reedTexture(): THREE.CanvasTexture {
  return canvasTexture((g, s) => {
    const r = mulberry32(41);
    for (let i = 0; i < 14; i++) {
      const x0 = s * (0.15 + r() * 0.7), lean = (r() - 0.5) * s * 0.35, h = s * (0.55 + r() * 0.42), w = s * (0.015 + r() * 0.02);
      const gr = g.createLinearGradient(0, s, 0, s - h);
      gr.addColorStop(0, '#1e2e14');
      gr.addColorStop(1, r() < 0.5 ? '#6e8a3a' : '#8a8a46');
      g.fillStyle = gr;
      g.beginPath();
      g.moveTo(x0 - w, s);
      g.quadraticCurveTo(x0 + lean * 0.3, s - h * 0.6, x0 + lean, s - h);
      g.quadraticCurveTo(x0 + lean * 0.3 + w * 0.5, s - h * 0.55, x0 + w, s);
      g.fill();
      if (r() < 0.3) {
        g.fillStyle = '#4a3420';
        g.beginPath();
        g.ellipse(x0 + lean * 0.85, s - h * 0.85, w * 1.8, s * 0.06, 0, 0, Math.PI * 2);
        g.fill();
      }
    }
  });
}

/** 포자 꽃(R4, c023): 가는 대 끝의 보라·연두 포자 방울 */
function sporeTexture(): THREE.CanvasTexture {
  return canvasTexture((g, s) => {
    const r = mulberry32(43);
    for (let i = 0; i < 9; i++) {
      const x0 = s * (0.15 + r() * 0.7), h = s * (0.3 + r() * 0.4), tip = x0 + (r() - 0.5) * s * 0.15;
      g.strokeStyle = '#2e4a1e';
      g.lineWidth = s * 0.02;
      g.beginPath();
      g.moveTo(x0, s);
      g.quadraticCurveTo(x0, s - h * 0.6, tip, s - h);
      g.stroke();
      const rr = s * (0.045 + r() * 0.04);
      const gr = g.createRadialGradient(tip, s - h, 0, tip, s - h, rr);
      const purple = r() < 0.6;
      gr.addColorStop(0, purple ? '#e6b8ff' : '#efffb0');
      gr.addColorStop(1, purple ? '#7a3aa8' : '#7ac83a');
      g.fillStyle = gr;
      g.beginPath();
      g.arc(tip, s - h, rr, 0, Math.PI * 2);
      g.fill();
    }
  });
}

/** 마른 사막 풀(R5, C2): 빛바랜 금빛 가는 잎 몇 포기 + 바닥 모래 둔덕(풀 포기 대신) */
function dryTexture(): THREE.CanvasTexture {
  return canvasTexture((g, s) => {
    const r = mulberry32(53);
    for (let i = 0; i < 3; i++) {
      const x = s * (0.3 + r() * 0.4), rr = s * (0.14 + r() * 0.1);
      g.fillStyle = r() < 0.5 ? '#d9a45a' : '#c99448';
      g.beginPath();
      g.ellipse(x, s * 0.97, rr, rr * 0.35, 0, Math.PI, 0);
      g.fill();
    }
    for (let i = 0; i < 11; i++) {
      const x0 = s * (0.22 + r() * 0.56), lean = (r() - 0.5) * s * 0.6, h = s * (0.35 + r() * 0.45), w = s * (0.012 + r() * 0.015);
      const gr = g.createLinearGradient(0, s, 0, s - h);
      gr.addColorStop(0, '#7a5a2a');
      gr.addColorStop(1, r() < 0.5 ? '#f0d48a' : '#d8b062');
      g.fillStyle = gr;
      g.beginPath();
      g.moveTo(x0 - w, s * 0.96);
      g.quadraticCurveTo(x0 + lean * 0.3, s - h * 0.6, x0 + lean, s - h);
      g.quadraticCurveTo(x0 + lean * 0.3 + w * 0.6, s - h * 0.55, x0 + w, s * 0.96);
      g.fill();
    }
  });
}

/** 흑요석 파편(R6, C2): 검은 유리 조각 몇 개 + 끝이 보라로 빛나는 작은 결정(풀 포기 대신) */
function shardTexture(): THREE.CanvasTexture {
  return canvasTexture((g, s) => {
    const r = mulberry32(61);
    for (let i = 0; i < 7; i++) {
      const x0 = s * (0.2 + r() * 0.6), h = s * (0.18 + r() * 0.4), w = s * (0.04 + r() * 0.05), lean = (r() - 0.5) * s * 0.3;
      const crystal = r() < 0.4;
      const gr = g.createLinearGradient(0, s * 0.95, 0, s * 0.95 - h);
      gr.addColorStop(0, crystal ? '#3a0e5a' : '#120c1a');
      gr.addColorStop(0.65, crystal ? '#9a3ae0' : '#3a3050');
      gr.addColorStop(1, crystal ? '#f2c4ff' : '#8a7aa8');
      g.fillStyle = gr;
      g.beginPath();
      g.moveTo(x0 - w, s * 0.95);
      g.lineTo(x0 + lean, s * 0.95 - h);
      g.lineTo(x0 + w, s * 0.95);
      g.fill();
    }
  });
}

/** 교차한 두 장의 판(바닥이 원점) */
function crossCard(): THREE.BufferGeometry {
  const a = new THREE.PlaneGeometry(1, 1);
  a.translate(0, 0.5, 0);
  const b = a.clone();
  b.rotateY(Math.PI / 2);
  const parts = [a, b];
  const g = new THREE.BufferGeometry();
  const pos: number[] = [], uvs: number[] = [], idx: number[] = [];
  let base = 0;
  for (const p of parts) {
    const pp = p.getAttribute('position'), uu = p.getAttribute('uv'), ii = p.getIndex()!;
    for (let i = 0; i < pp.count; i++) { pos.push(pp.getX(i), pp.getY(i), pp.getZ(i)); uvs.push(uu.getX(i), uu.getY(i)); }
    for (let i = 0; i < ii.count; i++) idx.push(ii.getX(i) + base);
    base += pp.count;
    p.dispose();
  }
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(new Array(pos.length).fill(0).map((_, i) => (i % 3 === 1 ? 1 : 0)), 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  g.setIndex(idx);
  return g;
}

/** 풀·꽃 카드 인스턴싱(바람에 끝만 흔들린다, 법선은 위로 고정해 땅과 같은 밝기) */
export type CardKind = 'tuft' | 'flower' | 'frost' | 'reed' | 'spore' | 'dry' | 'shard';
export function cardField(items: readonly Emitter[], kind: CardKind, warm: boolean): THREE.InstancedMesh | null {
  if (items.length === 0) return null;
  const tex = kind === 'tuft' ? tuftTexture() : kind === 'flower' ? flowerTexture() : kind === 'frost' ? frostTexture() : kind === 'reed' ? reedTexture() : kind === 'dry' ? dryTexture() : kind === 'shard' ? shardTexture() : sporeTexture();
  const m = new THREE.MeshStandardNodeMaterial({ map: tex, alphaTest: 0.45, side: THREE.DoubleSide, roughness: 1, metalness: 0 });
  const top = positionGeometry.y;
  const ph = positionLocal.x.mul(0.35).add(positionLocal.z.mul(0.27));
  // 서리 더미·흑요석 파편은 흔들리지 않는다
  const sway = kind === 'frost' || kind === 'shard' ? 0 : 1;
  m.positionNode = positionLocal.add(vec3(sin(time.mul(1.7).add(ph)).mul(0.07 * sway).mul(top), 0, cos(time.mul(1.3).add(ph)).mul(0.04 * sway).mul(top)));
  m.normalNode = transformNormalToView(vec3(0, 1, 0));
  const mesh = new THREE.InstancedMesh(crossCard(), m, items.length);
  const col = new THREE.Color();
  const arr = mesh.instanceMatrix.array as Float32Array;
  items.forEach((e, i) => {
    const grassy = kind === 'tuft' || kind === 'reed' || kind === 'dry';
    const s = e.s * (grassy ? 1.15 : kind === 'frost' || kind === 'shard' ? 1.0 : 0.9);
    compose(arr, i * 16, e.x, 0, e.z, e.v * Math.PI * 2, 0, s, s * (kind === 'tuft' ? 0.85 : kind === 'reed' ? 1.1 : kind === 'dry' ? 0.8 : kind === 'frost' || kind === 'shard' ? 0.7 : 0.75), s);
    if (kind === 'tuft') col.setRGB(0.8 + e.v * 0.35, 0.9 + e.v * 0.2, warm ? 0.75 : 0.7);
    else if (kind === 'reed') col.setRGB(0.85 + e.v * 0.25, 0.9 + e.v * 0.15, 0.8);
    else col.setRGB(1, 1, 1);
    mesh.setColorAt(i, col);
  });
  mesh.instanceMatrix.needsUpdate = true;
  mesh.receiveShadow = true;
  mesh.frustumCulled = false;
  return mesh;
}

/** 카메라를 향한 판: 법선(+z)을 시선 반대(0, sin p, cos p)로 돌린다(x축 -p 회전). bottomAtOrigin이면 아래 끝이 원점 */
/** 담쟁이 줄기: 위에서 늘어지는 덩굴 7가닥, 잎은 위쪽이 빽빽하고 아래로 갈수록 듬성듬성(세로 텍스처) */
function ivyTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 128; c.height = 256;
  const g = c.getContext('2d')!;
  const r = mulberry32(17);
  const leaf = (x: number, y: number, s: number, a: number, col: string): void => {
    g.save(); g.translate(x, y); g.rotate(a); g.fillStyle = col;
    g.beginPath(); g.moveTo(0, 0); g.quadraticCurveTo(s * 0.7, s * 0.35, 0, s); g.quadraticCurveTo(-s * 0.7, s * 0.35, 0, 0); g.fill();
    g.restore();
  };
  const cols = ['#2e5a22', '#3f7a2c', '#5a9a36', '#79b048', '#4a8a30'];
  for (let i = 0; i < 11; i++) {
    let x = 10 + r() * 108;
    const len = 256 * (0.45 + r() * 0.55);
    g.strokeStyle = '#3b4a22'; g.lineWidth = 2;
    g.beginPath(); g.moveTo(x, 0);
    for (let y = 0; y < len; y += 8) {
      x += (r() - 0.5) * 5;
      g.lineTo(x, y);
      const dens = 1 - y / 256;
      if (r() < 0.45 + dens * 0.55) leaf(x + (r() - 0.5) * 8, y, 12 + r() * 11 * (0.6 + dens * 0.6), (r() - 0.5) * 2.6, cols[(r() * cols.length) | 0]);
    }
    g.stroke();
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.generateMipmaps = true;
  return t;
}

/**
 * 담쟁이 카드: 윗끝이 원점인 세로 판(벽 면과 나란히), 아래 끝만 바람에 살짝 흔들린다. 1회 그리기.
 * 카메라 쪽 벽이 컷어웨이로 CUT_H까지 잘리면 그 벽에 달린 덩굴도 같은 식(kitCutNodes, 잘라내기 방식)으로 잘린다.
 * 전에는 벽만 낮아지고 덩굴은 3m 높이에 그대로 떠서 R1 방3 혼잡 장면의 앞쪽 적과 바닥을 가렸다(20261020-world-npc, 루프 D13·D14).
 */
export function ivyField(items: readonly (Emitter & { rot: number; w: number })[]): THREE.InstancedMesh | null {
  if (items.length === 0) return null;
  const geo = new THREE.PlaneGeometry(1, 1);
  geo.translate(0, -0.5, 0.0);
  const cutSrc = new Float32Array(items.length * 4);
  items.forEach((e, i) => { cutSrc[i * 4] = e.x; cutSrc[i * 4 + 1] = e.z; cutSrc[i * 4 + 2] = 1; cutSrc[i * 4 + 3] = 1; });
  geo.setAttribute('aCut', new THREE.InstancedBufferAttribute(cutSrc, 4));
  const m = new THREE.MeshStandardNodeMaterial({ map: ivyTexture(), alphaTest: 0.4, side: THREE.DoubleSide, roughness: 0.95, metalness: 0 });
  m.maskNode = kitCutNodes().keep;
  const hang = positionGeometry.y.negate();
  const ph = positionLocal.x.mul(0.5);
  m.positionNode = positionLocal.add(vec3(sin(time.mul(1.1).add(ph)).mul(0.03).mul(hang), 0, cos(time.mul(0.9).add(ph)).mul(0.03).mul(hang)));
  const mesh = new THREE.InstancedMesh(geo, m, items.length);
  const arr = mesh.instanceMatrix.array as Float32Array;
  const col = new THREE.Color();
  items.forEach((e, i) => {
    compose(arr, i * 16, e.x, e.y, e.z, e.rot, 0, e.w, e.s, 1);
    col.setRGB(0.85 + e.v * 0.3, 0.95 + e.v * 0.15, 0.8 + e.v * 0.2);
    mesh.setColorAt(i, col);
  });
  mesh.instanceMatrix.needsUpdate = true;
  mesh.receiveShadow = true;
  mesh.frustumCulled = false;
  return mesh;
}

function facingQuad(w: number, hgt: number, bottomAtOrigin: boolean): THREE.BufferGeometry {
  const g = new THREE.PlaneGeometry(w, hgt);
  if (bottomAtOrigin) g.translate(0, hgt / 2, 0);
  g.rotateX(FACE_CAMERA);
  return g;
}

function phaseAttr(n: number, items: readonly Emitter[]): THREE.InstancedBufferAttribute {
  const a = new Float32Array(n);
  items.forEach((e, i) => { a[i] = e.v; });
  return new THREE.InstancedBufferAttribute(a, 1);
}

/** 화로 불꽃: 잡음으로 일렁이는 물방울 모양, 가산 혼합(블룸이 잡도록 1보다 밝다) */
export function flameField(items: readonly Emitter[], hot: readonly [number, number, number] = [0.95, 0.22, 0.04], core: readonly [number, number, number] = [1.0, 0.62, 0.22]): THREE.InstancedMesh | null {
  if (items.length === 0) return null;
  const geo = facingQuad(0.75, 1.15, true);
  geo.setAttribute('aPhase', phaseAttr(items.length, items));
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, fog: false });
  const p = uv();
  const ph = attribute('aPhase', 'float');
  const t = time.mul(2.4).add(ph.mul(10));
  const n = mx_noise_float(vec3(p.x.mul(3.2), p.y.mul(2.4).sub(t), ph.mul(5)));
  const x = abs(p.x.sub(0.5).add(n.mul(0.12).mul(p.y)));
  const w = mix(float(0.34), float(0.04), p.y.pow(0.85));
  const body = float(1).sub(smoothstep(w.mul(0.35), w, x)).mul(smoothstep(0.0, 0.1, p.y)).mul(float(1).sub(smoothstep(0.5, 1.0, p.y.add(n.mul(0.15)))));
  const coreM = float(1).sub(smoothstep(0.0, w.mul(0.6), x)).mul(float(1).sub(smoothstep(0.2, 0.7, p.y)));
  m.colorNode = mix(vec3(hot[0], hot[1], hot[2]), vec3(core[0], core[1], core[2]), coreM).mul(body).mul(1.35);
  const mesh = new THREE.InstancedMesh(geo, m, items.length);
  const arr = mesh.instanceMatrix.array as Float32Array;
  items.forEach((e, i) => compose(arr, i * 16, e.x, e.y - 0.15, e.z, 0, 0, e.s, e.s, e.s));
  mesh.frustumCulled = false;
  mesh.renderOrder = 5;
  return mesh;
}

/** 발광 원판(버섯 청록, 마을 등불 주황): 부드러운 원판, 천천히 숨쉰다 */
export function glowField(items: readonly Emitter[], rgb: readonly [number, number, number] = [0.25, 0.85, 1.0]): THREE.InstancedMesh | null {
  if (items.length === 0) return null;
  const geo = facingQuad(1, 1, false);
  geo.setAttribute('aPhase', phaseAttr(items.length, items));
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, fog: false });
  const r = length(uv().sub(0.5));
  const a = float(1).sub(smoothstep(0.0, 0.5, r));
  const pulse = sin(time.mul(1.4).add(attribute('aPhase', 'float').mul(6.28))).mul(0.2).add(0.8);
  m.colorNode = vec3(rgb[0], rgb[1], rgb[2]).mul(a.mul(a)).mul(pulse).mul(1.15);
  const mesh = new THREE.InstancedMesh(geo, m, items.length);
  const arr = mesh.instanceMatrix.array as Float32Array;
  items.forEach((e, i) => compose(arr, i * 16, e.x, e.y, e.z, 0, 0, e.s, e.s, e.s));
  mesh.frustumCulled = false;
  mesh.renderOrder = 5;
  return mesh;
}

/** 빛줄기: 태양 쪽으로 기운 긴 판(시선에 수직), 가장자리·양 끝이 사라지는 가산 그라데이션 */
export function shaftField(items: readonly Emitter[], sun: THREE.Vector3, rgb: readonly [number, number, number] = [1.0, 0.84, 0.55], gain = 0.5): THREE.Mesh | null {
  if (items.length === 0) return null;
  const L = sun.clone().normalize();
  const p = (CAMERA.pitchDeg * Math.PI) / 180;
  const view = new THREE.Vector3(0, -Math.sin(p), -Math.cos(p));
  const side = new THREE.Vector3().crossVectors(L, view).normalize();
  const pos: number[] = [], uvs: number[] = [], ph: number[] = [], idx: number[] = [];
  const len = 17;
  items.forEach((e, i) => {
    const b = new THREE.Vector3(e.x, 0, e.z);
    const t = b.clone().addScaledVector(L, len);
    const wb = e.s * 0.45, wt = e.s * 1.1;
    const v = [b.clone().addScaledVector(side, -wb), b.clone().addScaledVector(side, wb), t.clone().addScaledVector(side, wt), t.clone().addScaledVector(side, -wt)];
    for (const q of v) pos.push(q.x, q.y, q.z);
    uvs.push(0, 0, 1, 0, 1, 1, 0, 1);
    ph.push(e.v, e.v, e.v, e.v);
    const o = i * 4;
    idx.push(o, o + 1, o + 2, o, o + 2, o + 3);
  });
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  g.setAttribute('aPhase', new THREE.Float32BufferAttribute(ph, 1));
  g.setIndex(idx);
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, fog: false });
  const q = uv();
  // 판 하나에 가는 광선 여러 줄(줄마다 굵기·세기가 다르다), 바닥 쪽은 옅게 시작한다
  const ph0 = attribute('aPhase', 'float');
  const across = float(1).sub(abs(q.x.sub(0.5)).mul(2)).pow(1.4);
  const rays = sin(q.x.mul(23).add(ph0.mul(17))).mul(0.5).add(0.5).pow(3).mul(0.75).add(sin(q.x.mul(9.0).add(ph0.mul(5))).mul(0.15).add(0.25));
  const along = smoothstep(0.04, 0.3, q.y).mul(float(1).sub(smoothstep(0.5, 1.0, q.y)));
  const pulse = sin(time.mul(0.55).add(attribute('aPhase', 'float').mul(6.28))).mul(0.25).add(0.75);
  m.colorNode = vec3(rgb[0], rgb[1], rgb[2]).mul(across.mul(rays).mul(along).mul(pulse).mul(gain));
  const mesh = new THREE.Mesh(g, m);
  mesh.frustumCulled = false;
  mesh.renderOrder = 6;
  return mesh;
}

/**
 * 청록 물웅덩이: 바닥에 붙은 원판. 가장자리는 잡음으로 들쭉날쭉, 가운데는 깊은 청록, 물결 반짝임은 실시간.
 * 발광을 조금 넣어 블룸이 물빛 번짐을 만든다. 1회 그리기(인스턴싱).
 */
export type PoolTheme = 'water' | 'ice' | 'poison' | 'oasis' | 'abyss';
/**
 * 웅덩이 색(c023): water = 지역1 청록 물, ice = 지역3 얼어붙은 못(창백한 청백 얼음, 균열 빛 없음 — 위험 칸 서리 균열과 구분),
 * poison = 지역4 탁한 늪물 + 가장자리 연두 발광(위험 칸 독 늪의 밝은 연두 기포보다 어둡고 작게 — 장식임이 읽히게)
 */
const POOL_PAL: Record<PoolTheme, { shallow: readonly number[]; deep: readonly number[]; band: readonly number[]; eShallow: readonly number[]; eDeep: readonly number[]; glint: readonly number[] }> = {
  water: { shallow: [0.06, 0.27, 0.25], deep: [0.008, 0.045, 0.055], band: [0.025, 0.09, 0.08], eShallow: [0.045, 0.3, 0.3], eDeep: [0.0, 0.12, 0.16], glint: [0.6, 0.95, 0.9] },
  ice: { shallow: [0.55, 0.7, 0.86], deep: [0.18, 0.32, 0.52], band: [0.75, 0.84, 0.95], eShallow: [0.08, 0.16, 0.26], eDeep: [0.02, 0.07, 0.14], glint: [0.85, 0.95, 1.0] },
  poison: { shallow: [0.1, 0.2, 0.06], deep: [0.02, 0.05, 0.03], band: [0.05, 0.09, 0.03], eShallow: [0.16, 0.36, 0.04], eDeep: [0.01, 0.05, 0.02], glint: [0.7, 1.0, 0.3] },
  // oasis = 지역5 청록 오아시스 샘(금빛 사암 바닥과 보색, 위험 칸 유사의 짙은 황토 소용돌이와 구분), abyss = 지역6 허공이 비치는 남보라 웅덩이(균열 빛 없이 별빛만 — 위험 칸 자홍 균열과 구분)
  oasis: { shallow: [0.1, 0.42, 0.4], deep: [0.01, 0.12, 0.16], band: [0.32, 0.2, 0.08], eShallow: [0.05, 0.34, 0.32], eDeep: [0.0, 0.1, 0.14], glint: [1.0, 0.92, 0.6] },
  abyss: { shallow: [0.12, 0.06, 0.22], deep: [0.01, 0.0, 0.04], band: [0.06, 0.04, 0.1], eShallow: [0.1, 0.04, 0.22], eDeep: [0.02, 0.0, 0.06], glint: [0.85, 0.7, 1.0] },
};
const v3 = (a: readonly number[]) => vec3(a[0], a[1], a[2]);
export function poolField(items: readonly Emitter[], theme: PoolTheme = 'water'): THREE.InstancedMesh | null {
  const pal = POOL_PAL[theme];
  if (items.length === 0) return null;
  const geo = new THREE.CircleGeometry(1, 40);
  geo.rotateX(-Math.PI / 2);
  geo.setAttribute('aPhase', phaseAttr(items.length, items));
  // 거칠기를 높여(0.4 → 0.75) 낮은 해의 반사가 물을 하얗게 덮지 않게 하고, 청록은 발광으로 낸다
  const m = new THREE.MeshStandardNodeMaterial({ transparent: true, depthWrite: false, roughness: 0.75, metalness: 0 });
  const q = uv().sub(0.5).mul(2);
  const ph = attribute('aPhase', 'float');
  const r = length(q);
  const edgeN = mx_noise_float(vec3(q.mul(2.2), ph.mul(9))).mul(0.18);
  // 물(r < 약 0.72) + 바깥의 젖은 돌 테(어둡고 축축하게, 패인 웅덩이처럼 보이게)
  const rr = r.add(edgeN);
  const body = float(1).sub(smoothstep(0.88, 1.0, rr));
  const water = float(1).sub(smoothstep(0.55, 0.74, rr));
  // 중심은 깊고 어두운 청록, 가장자리는 얕은 물과 젖은 둑으로 읽히게 한다.
  const depth = float(1).sub(smoothstep(0.04, 0.62, rr));
  const shoreBand = smoothstep(0.62, 0.76, rr).mul(float(1).sub(smoothstep(0.76, 0.92, rr)));
  const wp = positionWorld.xz;
  const ripple = mx_noise_float(vec3(wp.mul(1.6), time.mul(0.6).add(ph.mul(4)))).mul(0.5).add(0.5);
  const glint = smoothstep(0.84, 0.95, ripple).mul(depth.mul(0.6).add(0.4));
  // 햇빛을 받으면 톤매핑에서 채도가 빠지므로 반사율을 낮게 두고 청록은 발광으로 낸다(콘셉트의 맑은 청록 물빛)
  // 가장자리는 얕은 물(밝은 청록), 가운데는 깊은 물(짙은 청록)
  const wet = theme === 'ice' ? vec3(0.6, 0.68, 0.8) : theme === 'oasis' ? vec3(0.42, 0.3, 0.14) : vec3(0.07, 0.08, 0.06);
  const waterCol = mix(v3(pal.shallow), v3(pal.deep), depth);
  // 어두운 띠가 물과 돌의 경계를 붙이고, 중심은 바닥 색에 묻히지 않을 만큼만 불투명하게 둔다.
  m.colorNode = mix(mix(wet, waterCol, water), v3(pal.band), shoreBand.mul(0.72));
  m.emissiveNode = mix(v3(pal.eShallow), v3(pal.eDeep), depth).mul(0.72).mul(water).add(v3(pal.glint).mul(glint.mul(theme === 'poison' ? 0.45 : 0.32)).mul(water));
  // 테는 반투명(바닥 돌이 비쳐 젖은 돌로 읽힌다), 중심 물은 깊이 색이 우선한다.
  m.opacityNode = body.mul(mix(float(0.48), float(0.9), water)).mul(float(1).sub(shoreBand.mul(0.16)));
  const mesh = new THREE.InstancedMesh(geo, m, items.length);
  const arr = mesh.instanceMatrix.array as Float32Array;
  items.forEach((e, i) => compose(arr, i * 16, e.x, 0.025, e.z, e.v * Math.PI * 2, 0, e.s, 1, e.s * (0.7 + e.v * 0.3)));
  mesh.frustumCulled = false;
  mesh.receiveShadow = true;
  mesh.renderOrder = 1;
  return mesh;
}

/** 접지 그림자 재질: 가운데가 진하고 바깥으로 부드럽게 사라지는 검은 원판(빛 방향과 무관한 접촉 어둠) */
export function contactMaterial(strength = 0.5, perInstance = false): THREE.MeshBasicNodeMaterial {
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, fog: false });
  const r = length(uv().sub(0.5).mul(2));
  // 안쪽 절반은 고르게 진하고 바깥 절반에서 사라진다(소품 발자국 가장자리 바로 밖이 가장 눈에 띄는 접촉 어둠)
  const a = float(1).sub(smoothstep(0.45, 1.0, r));
  m.colorNode = vec3(0.02, 0.02, 0.03);
  const base = a.mul(a).mul(float(3).sub(a.mul(2))).mul(strength);
  m.opacityNode = perInstance ? base.mul(attribute('aOp', 'float')) : base;
  // 바닥·물웅덩이보다 위, 소품 아래에 겹쳐 그린다(깊이 쓰기 없음 → 겹쳐도 무늬가 안 생긴다)
  m.polygonOffset = true;
  m.polygonOffsetFactor = -2;
  m.polygonOffsetUnits = -2;
  return m;
}

/** 접지 그림자 원판들: 소품이 바닥에 떠 보이지 않게 발밑을 어둡게 한다. 1회 그리기(인스턴싱) */
export function contactField(items: readonly { x: number; y: number; z: number; rx: number; rz: number; rot: number; a: number }[]): THREE.InstancedMesh | null {
  if (items.length === 0) return null;
  const geo = new THREE.PlaneGeometry(2, 2);
  geo.rotateX(-Math.PI / 2);
  const op = new Float32Array(items.length);
  items.forEach((e, i) => { op[i] = e.a; });
  geo.setAttribute('aOp', new THREE.InstancedBufferAttribute(op, 1));
  const m = contactMaterial(1, true);
  const mesh = new THREE.InstancedMesh(geo, m, items.length);
  const arr = mesh.instanceMatrix.array as Float32Array;
  items.forEach((e, i) => compose(arr, i * 16, e.x, e.y + 0.03, e.z, e.rot, 0, e.rx, 1, e.rz));
  mesh.frustumCulled = false;
  mesh.renderOrder = 2;
  mesh.name = 'contact-shadows';
  return mesh;
}

/**
 * 대기 입자(c023·C2): 지역3 눈송이·지역4 포자·지역5 모래 먼지(옆으로 흩날림)·지역6 보라 불씨(위로 떠오름, 가산). 카메라가 보는 바닥 가운데(center, cull이 갱신) 둘레 상자 안에서
 * 셰이더가 시간으로 위치를 감아 돌린다(인스턴스 1개 = 드로우콜 +1, 프레임 할당 0, CPU 갱신 없음).
 * 크기는 작고(눈 4~9cm, 포자 5~10cm, 먼지 3~8cm, 불씨 4~9cm) 불씨만 가산이고 밝기는 천천히 흐르기만 해 섬광이 없다.
 */
export function driftField(kind: DriftKind, density: number): { mesh: THREE.InstancedMesh; center: { value: THREE.Vector3 } } | null {
  const n = driftCount(kind, density);
  if (n === 0) return null;
  const geo = facingQuad(1, 1, false);
  const seed = new Float32Array(n * 4);
  const r = mulberry32(kind === 'snow' ? 77 : kind === 'spore' ? 78 : kind === 'dust' ? 79 : 80);
  for (let i = 0; i < n; i++) { seed[i * 4] = r(); seed[i * 4 + 1] = r(); seed[i * 4 + 2] = r(); seed[i * 4 + 3] = r(); }
  geo.setAttribute('aSeed', new THREE.InstancedBufferAttribute(seed, 4));
  const center = uniform(new THREE.Vector3());
  const ember = kind === 'ember';
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, fog: false, blending: ember ? THREE.AdditiveBlending : THREE.NormalBlending });
  const sd = attribute('aSeed', 'vec4');
  const box = kind === 'snow' ? vec3(46, 11, 34) : kind === 'dust' ? vec3(46, 3.2, 34) : kind === 'ember' ? vec3(40, 7, 30) : vec3(40, 4.5, 30);
  const fall = kind === 'snow' ? float(0.9).add(sd.w.mul(0.6)) : kind === 'dust' ? float(1.6).add(sd.w.mul(1.4)) : kind === 'ember' ? float(0.35).add(sd.w.mul(0.3)) : float(0.12).add(sd.w.mul(0.1));
  const t = time.mul(fall);
  // 상자 안 위치(0..1): 눈은 아래로 떨어지고 옆으로 흔들린다, 포자·불씨는 위로 떠오르며 맴돈다,
  // 모래 먼지는 바닥 가까이서 바람(+x, 낮은 해 쪽)을 따라 옆으로 흩날린다
  const wind = kind === 'dust' ? t.div(box.x) : float(0);
  const fx = fract(sd.x.add(wind).add(sin(t.mul(0.7).add(sd.y.mul(20))).mul(0.012)).sub(center.x.div(box.x)));
  const fz = fract(sd.z.add(cos(t.mul(0.5).add(sd.x.mul(20))).mul(0.012)).sub(center.z.div(box.z)));
  const fy = kind === 'snow' ? fract(sd.y.sub(t.div(box.y))) : kind === 'dust' ? fract(sd.y.add(sin(t.mul(0.9).add(sd.z.mul(30))).mul(0.06))) : fract(sd.y.add(t.div(box.y)));
  const world = vec3(fx.sub(0.5).mul(box.x).add(center.x), fy.mul(box.y).add(kind === 'snow' ? 0.2 : kind === 'dust' ? 0.05 : 0.15), fz.sub(0.5).mul(box.z).add(center.z));
  const size = kind === 'snow' ? float(0.05).add(sd.w.mul(0.05)) : kind === 'dust' ? float(0.035).add(sd.w.mul(0.04)) : kind === 'ember' ? float(0.04).add(sd.w.mul(0.05)) : float(0.06).add(sd.w.mul(0.05));
  m.positionNode = positionLocal.mul(size).add(world);
  const rr = length(uv().sub(0.5));
  const a = float(1).sub(smoothstep(0.15, 0.5, rr));
  // 상자 위·아래 끝에서 서서히 사라진다(감길 때 툭 나타나지 않게). 모래 먼지는 좌우로 감기므로 위아래만
  const edge = smoothstep(0.0, 0.12, fy).mul(float(1).sub(smoothstep(0.85, 1.0, fy)));
  if (kind === 'snow') m.colorNode = vec3(0.92, 0.96, 1.0);
  else if (kind === 'dust') m.colorNode = mix(vec3(0.95, 0.76, 0.44), vec3(1.0, 0.9, 0.66), step01(sd.x));
  // 불씨: 위로 갈수록 식는다(자홍 → 보라), 점멸 없이 밝기만 천천히 흐른다
  else if (ember) m.colorNode = mix(vec3(1.5, 0.5, 2.2), vec3(0.55, 0.25, 1.3), fy).mul(sin(time.mul(1.3).add(sd.x.mul(30))).mul(0.15).add(0.85));
  else m.colorNode = mix(vec3(0.72, 0.42, 1.0), vec3(0.75, 1.0, 0.4), step01(sd.x));
  m.opacityNode = a.mul(edge).mul(kind === 'snow' ? 0.85 : kind === 'dust' ? 0.55 : ember ? 0.9 : 0.75);
  const mesh = new THREE.InstancedMesh(geo, m, n);
  // 인스턴스 행렬은 단위 행렬(위치는 셰이더가 정한다)
  mesh.frustumCulled = false;
  mesh.renderOrder = 7;
  mesh.name = 'drift-' + kind;
  return { mesh, center };
}
function step01(x: THREE.Node<'float'>): THREE.Node<'float'> {
  return smoothstep(0.55, 0.56, x);
}
